import { completionProviders } from '@app/shared/workflow-commands/workflow-completion-requirements';
import { isWorkflowFormLayoutSupported, isWorkflowFormVersionAvailable, resolveWorkflowFormLayout } from '@app/shared/workflow-commands/workflow-form-layout';
import {
  requireWorkflowPayloadVersion,
  type WorkflowDefinitionDocument,
  type WorkflowCapabilityReference,
} from './platform-workflow-definition-lifecycle.models';
import {
  addWorkflowCapability,
  capabilityIdentity,
  installedReference,
  sameCapabilityReference,
  capabilityReferencesShapeError,
  type WorkflowCapabilityCatalogEntry,
} from './workflow-capability-authoring';
import { applyRequirementDetailDefaults } from './workflow-requirement-detail-defaults';
import {
  addStepCommand,
  initializeBlankStep,
  type StepAuthoringCatalog,
  type StepAuthoringCommand,
  type StepCommand,
} from './workflow-step-composition';
import { readStepOutcomeRoutes } from './workflow-step-outcome-routes';

/** Prepare one complete review setup in memory; callers commit it together with the selected paths. */
export function addReviewDecision(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  catalog: StepAuthoringCatalog,
  capabilities: WorkflowCapabilityCatalogEntry[],
  exactRecordRef?: WorkflowCapabilityReference,
): WorkflowDefinitionDocument {
  if (exactRecordRef && (capabilityReferencesShapeError([exactRecordRef]) || !isRecordReference(exactRecordRef)
    || ![1, 2].includes(exactRecordRef.key.version))) throw new Error('Choose an exact installed Record review v1 or v2 action.');
  const installed = (code: string, version = 1) => {
    const matches = catalog.commands.filter(
      (command) =>
        command.capability.key.provider === 'workflow' &&
        command.capability.key.kind === 'command' &&
        command.capability.key.code === code &&
        command.capability.key.version === version,
    );
    if (matches.length !== 1)
      throw new Error(
        'Review setup is unavailable or ambiguous. Reload installed actions before adding a review decision.',
      );
    return matches[0];
  };
  const request = installed('workflow.review.request');
  const record = installed('workflow.review.record', exactRecordRef?.key.version ?? 1);
  if (exactRecordRef && !sameCapabilityReference(record.capability, exactRecordRef))
    throw new Error('The selected Record review contract changed. Reload installed actions before adding it.');
  const complete = installed('workflow.step.complete');
  const evidence = record.decisionEvidence;
  const outcomes = record.outcomes.map((outcome) => outcome.code);
  if (
    catalog.version !== 1 ||
    request.completesStep !== false ||
    request.outcomes.length ||
    record.completesStep !== false ||
    record.capability.key.version === 2 && (record.form?.contract !== 'WorkflowReviewRecordRequest'
      || record.form.version !== 1 || record.form.inputSchemaHash !== record.capability.inputSchemaHash) ||
    !evidence ||
    !evidence.decisionKind ||
    !outcomes.length ||
    outcomes.length !== 2 || !['approved', 'changes_required'].every(code => outcomes.includes(code)) ||
    new Set(outcomes).size !== outcomes.length ||
    evidence.outcomeCodes.length !== outcomes.length ||
    new Set(evidence.outcomeCodes).size !== outcomes.length ||
    outcomes.some((code) => !evidence.outcomeCodes.includes(code)) ||
    complete.completesStep === false ||
    complete.supportsCompletionRequirements !== true ||
    complete.outcomes.length ||
    record.condition?.code !== 'review.result' ||
    record.condition.version !== 1 ||
    ![request, record, complete].every((command) =>
      record.condition!.requiredCapabilities.some((reference) =>
        sameCapabilityReference(reference, command.capability),
      ),
    ) ||
    !evidence.requiredCommands?.some((reference) =>
      sameCapabilityReference(reference, request.capability),
    )
  )
    throw new Error(
      'The installed review actions do not support a complete recorded-decision setup. Reload actions or ask your administrator to check process support.',
    );
  const providers = catalog.requirementProviders?.filter(
    (provider) =>
      provider.provider === completionProviders.decision &&
      provider.version === 1 &&
      provider.settingsVersion === 1 &&
      !!provider.settingsHash,
  );
  if (providers?.length !== 1)
    throw new Error(
      'The recorded-decision requirement is unavailable or ambiguous. Reload requirements before adding a review decision.',
    );
  const provider = providers[0];
  const interactions = catalog.interactions.filter((interaction) =>
    sameCapabilityReference(interaction.command, record.capability),
  );
  if (
    interactions.length !== 1 ||
    interactions[0].code !== 'generated-command-form' ||
    interactions[0].version !== 1
  )
    throw new Error(
      'The installed Record review staff form is unavailable or ambiguous. Reload actions before adding this setup.',
    );
  let next = initializeBlankStep(document, nodeId);
  const existingRecords = next.nodes.find(node => node.id === nodeId)!.declaration!.behavior.commands
    .filter(command => isRecordReference(command.capability));
  if (existingRecords.some(command => !sameCapabilityReference(command.capability, record.capability)))
    throw new Error('This step already uses a different Record review version. Use its explicit compatible upgrade before changing versions.');
  for (const contract of [request, record, complete]) {
    const entries = capabilities.filter(
      (entry) =>
        capabilityIdentity(entry.key) ===
        capabilityIdentity(contract.capability.key),
    );
    if (
      entries.length !== 1 ||
      !sameCapabilityReference(
        installedReference(entries[0]),
        contract.capability,
      )
    )
      throw new Error(
        'An installed review contract has changed. Reload actions before adding this setup.',
      );
    next = addWorkflowCapability(next, entries[0], capabilities);
    const commands = next.nodes.find((node) => node.id === nodeId)!.declaration!
      .behavior.commands;
    const attached = commands.filter(
      (command) =>
        capabilityIdentity(command.capability.key) ===
        capabilityIdentity(contract.capability.key),
    );
    if (
      attached.length > 1 ||
      (attached.length &&
        !sameCapabilityReference(attached[0].capability, contract.capability))
    )
      throw new Error(
        'This step has an older or duplicate review action. Inspect the saved actions before adding a review decision.',
      );
    if (!attached.length)
      next = addStepCommand(next, nodeId, contract, capabilities);
    else
      for (const reference of contract.condition?.requiredCapabilities ?? []) {
        const dependency = capabilities.filter(
          (entry) =>
            capabilityIdentity(entry.key) === capabilityIdentity(reference.key),
        );
        if (
          dependency.length !== 1 ||
          !sameCapabilityReference(installedReference(dependency[0]), reference)
        )
          throw new Error(
            'A review routing dependency has changed. Reload actions before adding this setup.',
          );
        next = addWorkflowCapability(next, dependency[0], capabilities);
      }
  }
  const declaration = next.nodes.find(
    (node) => node.id === nodeId,
  )!.declaration!;
  const source = declaration.behavior.commands.find((command) =>
    sameCapabilityReference(command.capability, record.capability),
  )!;
  if (
    source.outcomes.length !== outcomes.length ||
    outcomes.some(
      (code) => !source.outcomes.some((outcome) => outcome.code === code),
    )
  )
    throw new Error(
      'The saved Record review action must include every installed review result. Edit its results before adding a completion requirement.',
    );
  const requirements = declaration.behavior.completionRequirements ?? [];
  if (
    new Set(requirements.map((requirement) => requirement.id)).size !==
    requirements.length
  )
    throw new Error(
      'This step has duplicate completion requirement identities. Inspect its requirements first.',
    );
  const bindings = requirements.filter((requirement) => {
    if (requirement.provider !== completionProviders.decision) return false;
    const reference = requirement.settings?.['sourceCommand'];
    return (
      !!reference &&
      typeof reference === 'object' &&
      'key' in reference &&
      !!reference.key &&
      typeof reference.key === 'object' &&
      capabilityIdentity(reference.key as typeof record.capability.key) ===
        capabilityIdentity(record.capability.key)
    );
  });
  if (bindings.length > 1)
    throw new Error(
      'More than one requirement uses this review decision. Inspect its completion requirements first.',
    );
  if (bindings.length) {
    const binding = bindings[0],
      saved = binding.settings['outcomeCodes'];
    if (
      binding.version !== provider.version ||
      binding.settingsVersion !== provider.settingsVersion ||
      binding.settingsHash !== provider.settingsHash ||
      Object.keys(binding.settings).some(
        (key) =>
          !['sourceCommand', 'decisionKind', 'outcomeCodes'].includes(key),
      ) ||
      !sameCapabilityReference(
        binding.settings['sourceCommand'] as typeof record.capability,
        record.capability,
      ) ||
      binding.settings['decisionKind'] !== evidence.decisionKind ||
      !Array.isArray(saved) ||
      saved.length !== outcomes.length ||
      new Set(saved).size !== outcomes.length ||
      outcomes.some((code) => !saved.includes(code))
    )
      throw new Error(
        'The existing decision requirement accepts different review results or uses an older contract. Include every installed result in that requirement before adding review paths.',
      );
  }
  const binding = bindings[0] ?? {
    id: `requirement-${crypto.randomUUID()}`,
    provider: provider.provider,
    version: provider.version,
    settingsVersion: provider.settingsVersion,
    settingsHash: provider.settingsHash,
    settings: {
      sourceCommand: structuredClone(record.capability),
      decisionKind: evidence.decisionKind,
      outcomeCodes: [...outcomes],
    },
  };
  next = {
    ...next,
    payloadSchemaVersion: requireWorkflowPayloadVersion(
      next.payloadSchemaVersion,
      '1.8',
    ),
    nodes: next.nodes.map((node) =>
      node.id !== nodeId
        ? node
        : {
            ...node,
            declaration: {
              ...declaration,
              behavior: {
                ...declaration.behavior,
                completionRequirements: bindings.length
                  ? requirements
                  : [...requirements, binding],
              },
            },
          },
    ),
  };
  readStepOutcomeRoutes(next, nodeId, source, record);
  return applyRequirementDetailDefaults(next, nodeId, catalog);
}

function isRecordReference(reference: WorkflowCapabilityReference): boolean {
  return reference?.key?.provider === 'workflow' && reference.key.kind === 'command' && reference.key.code === 'workflow.review.record';
}

const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
function hasOldRecordReference(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(hasOldRecordReference);
  if (!object(value)) return false;
  const key = value['key'];
  if (object(key) && key['provider'] === 'workflow' && key['kind'] === 'command'
    && key['code'] === 'workflow.review.record' && key['version'] === 1) return true;
  return Object.values(value).some(hasOldRecordReference);
}

function requireCompatibleReviewInput(document: WorkflowDefinitionDocument, command: StepCommand, target: StepAuthoringCommand): void {
  const refuse = () => { throw new Error('The saved review form has incompatible or opaque input settings. Repair its current settings before upgrading; no values were changed.'); };
  const fields = target.formFields;
  if (command.captureForm != null || command.inputBindings?.length || command.outputBindings?.length || !fields
    || fields.length !== 2 || new Set(fields.map(field => field.name)).size !== 2
    || !['outcome', 'notes'].every(name => fields.some(field => field.name === name))
    || command.requirements.some(code => !target.requirements.some(rule => rule.code === code))
    || target.requirements.some(rule => rule.mandatory && !command.requirements.includes(rule.code))
    || command.outcomes.some(outcome => !target.outcomes.some(known => known.code === outcome.code && known.reasonPolicies.includes(outcome.reasonPolicy)))) refuse();
  const configuration = command.formConfiguration;
  if (configuration == null) return;
  if (!object(configuration) || Object.keys(configuration).some(key => !['version', 'fields'].includes(key))
    || ![1, 2].includes(configuration.version) || !isWorkflowFormVersionAvailable(document.payloadSchemaVersion, configuration)
    || !Array.isArray(configuration.fields) || configuration.fields.length > 50
    || new Set(configuration.fields.map(field => field?.name)).size !== configuration.fields.length) refuse();
  const configuredKeys = ['name', 'label', 'required', 'maxLength', 'control', 'optionSource', 'options', 'layout', 'lookup'];
  for (const setting of configuration.fields) {
    if (!object(setting) || Object.keys(setting).some(key => !configuredKeys.includes(key))) refuse();
    const installed = fields!.find(field => field.name === setting.name);
    if (!installed || setting.lookup != null || setting.label != null && (typeof setting.label !== 'string' || !setting.label.trim() || setting.label.length > 120)
      || setting.required != null && (typeof setting.required !== 'boolean' || installed.required && !setting.required)
      || setting.maxLength != null && (!Number.isSafeInteger(setting.maxLength) || setting.maxLength < 1 || setting.maxLength > (installed.maxLength ?? 100000))
      || !isWorkflowFormLayoutSupported(configuration.version, setting.layout) || setting.layout != null && installed.supportsLayout !== true) refuse();
    const control = setting.control ?? installed!.control;
    const source = setting.optionSource ?? (installed!.control === 'select' ? 'contract-options' : null);
    if (!['text', 'textarea', 'select'].includes(control) || installed!.control === 'select' && control !== 'select'
      || source != null && !installed!.optionSources.includes(source) || source === 'lookup'
      || control !== 'select' && (setting.optionSource != null || setting.options != null)
      || control === 'select' && source == null || setting.options != null && source !== 'custom-options') refuse();
    const options = source === 'custom-options' ? setting.options : source === 'step-outcomes' ? command.outcomes.map(outcome => ({ value: outcome.code, label: outcome.label })) : installed!.options;
    if (control === 'select' && (!Array.isArray(options) || options.length < 1 || options.length > 100
      || options.some(option => !object(option) || Object.keys(option).some(key => !['value', 'label'].includes(key))
        || typeof option.value !== 'string' || !option.value.trim() || option.value.length > (setting.maxLength ?? installed!.maxLength ?? 4000)
        || typeof option.label !== 'string' || !option.label.trim() || option.label.length > 120)
      || new Set(options.map(option => option.value)).size !== options.length)) refuse();
  }
  if (!resolveWorkflowFormLayout(configuration.version, fields!.map(field => {
    const setting = configuration.fields.find(item => item.name === field.name);
    return { control: setting?.control ?? field.control, layout: setting?.layout };
  }))) refuse();
}

/** Explicitly upgrade one compatible saved v1 decision; every unrelated reference remains pinned. */
export function upgradeReviewDecision(document: WorkflowDefinitionDocument, nodeId: string, catalog: StepAuthoringCatalog,
  capabilities: WorkflowCapabilityCatalogEntry[], exactRecordRef: WorkflowCapabilityReference): WorkflowDefinitionDocument {
  if (capabilityReferencesShapeError([exactRecordRef]) || !isRecordReference(exactRecordRef) || exactRecordRef.key.version !== 2)
    throw new Error('Choose the exact installed Record review v2 action for this upgrade.');
  const targets = catalog.commands.filter(command => sameCapabilityReference(command.capability, exactRecordRef));
  const oldContracts = catalog.commands.filter(command => isRecordReference(command.capability) && command.capability.key.version === 1);
  const nodes = document.nodes.filter(node => node.id === nodeId);
  if (targets.length !== 1 || oldContracts.length !== 1 || nodes.length !== 1 || nodes[0].typeCode !== 'human_step' || !nodes[0].declaration)
    throw new Error('The saved or installed review setup is unavailable or ambiguous. Reload it before upgrading.');
  const node = nodes[0], declaration = node.declaration!, target = targets[0], old = oldContracts[0].capability;
  const records = declaration.behavior.commands.filter(command => isRecordReference(command.capability));
  if (records.length !== 1 || !sameCapabilityReference(records[0].capability, old))
    throw new Error('Upgrade requires one exact saved Record review v1 action. Mixed or changed versions are preserved.');
  const oldEntries = capabilities.filter(entry => capabilityIdentity(entry.key) === capabilityIdentity(old.key));
  if (oldEntries.length !== 1 || !sameCapabilityReference(installedReference(oldEntries[0]), old)
    || capabilityReferencesShapeError(document.capabilityReferences)
    || document.capabilityReferences?.filter(reference => sameCapabilityReference(reference, old)).length !== 1)
    throw new Error('The saved Record review v1 contract is not an exact installed pin. Its settings are preserved.');
  requireCompatibleReviewInput(document, records[0], target);
  const providers = catalog.requirementProviders?.filter(provider => provider.provider === completionProviders.decision && provider.version === 1
    && provider.settingsVersion === 1 && !!provider.settingsHash) ?? [];
  const replace = (reference: WorkflowCapabilityReference): WorkflowCapabilityReference => {
    if (!isRecordReference(reference) || reference.key.version !== 1) return reference;
    if (!sameCapabilityReference(reference, old)) throw new Error('An attached review reference uses a changed v1 contract. Its settings are preserved.');
    return structuredClone(exactRecordRef);
  };
  const completionRequirements = declaration.behavior.completionRequirements?.map(requirement => {
    const reference = requirement.settings?.['sourceCommand'];
    if (!hasOldRecordReference(reference)) return requirement;
    const settings = requirement.settings, outcomes = settings['outcomeCodes'];
    if (requirement.provider !== completionProviders.decision || providers.length !== 1 || requirement.version !== 1 || requirement.settingsVersion !== 1
      || requirement.settingsHash !== providers[0].settingsHash || Object.keys(settings).some(key => !['sourceCommand', 'decisionKind', 'outcomeCodes'].includes(key))
      || !object(reference) || !sameCapabilityReference(reference as unknown as WorkflowCapabilityReference, old)
      || settings['decisionKind'] !== target.decisionEvidence?.decisionKind || !Array.isArray(outcomes) || outcomes.length !== 2
      || new Set(outcomes).size !== 2 || !['approved', 'changes_required'].every(code => outcomes.includes(code)))
      throw new Error('The saved decision requirement has incompatible or opaque settings. Repair it before upgrading; no settings were changed.');
    return { ...requirement, settings: { ...settings, sourceCommand: replace(old) } };
  });
  const interaction = declaration.detail.mainInteraction;
  if (interaction && hasOldRecordReference(interaction.command) && (interaction.code !== 'generated-command-form' || interaction.version !== 1))
    throw new Error('The saved review interaction is not the installed generated form. Its settings are preserved.');
  const nextNode = { ...node, declaration: { ...declaration,
    behavior: { ...declaration.behavior, commands: declaration.behavior.commands.map(command => command === records[0]
      ? { ...command, capability: replace(old) } : command),
      ...(completionRequirements == null ? {} : { completionRequirements }) },
    detail: { ...declaration.detail, ...(interaction ? { mainInteraction: { ...interaction, command: replace(interaction.command) } } : {}) },
    ...(declaration.dashboards == null ? {} : { dashboards: declaration.dashboards.map(dashboard => ({ ...dashboard,
      quickActions: dashboard.quickActions.map(replace) })) }),
  } };
  if (hasOldRecordReference(nextNode))
    throw new Error('Another attached setting refers to Record review v1. Resolve that dependency explicitly before upgrading; its content is preserved.');
  return addReviewDecision({ ...document, nodes: document.nodes.map(candidate => candidate === node ? nextNode : candidate) },
    nodeId, catalog, capabilities, exactRecordRef);
}
